/**
 * ブラウザのパスキー（WebAuthn）の呼び出し。サーバーは JSON（バイト列は base64url）で options を返し、同じ形の JSON を受け取る
 * （apps/server/src/lib/webauthn.ts の RegistrationCredentialJson / AssertionCredentialJson）。
 *
 * WHY PublicKeyCredential.parseCreationOptionsFromJSON を使わないか: Safari / 古い Chrome に無い（2026 時点でも揃っていない）。
 * 変換は base64url ↔ バイト列だけなので手で書く（依存を足さない方針）。
 */
import { fromBase64Url, toBase64Url } from './moneyLogic';

export const passkeysSupported = () => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;

interface CreationJson {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
  timeout?: number;
  excludeCredentials?: Array<{ type: 'public-key'; id: string }>;
}

interface RequestJson {
  challenge: string;
  rpId: string;
  allowCredentials?: Array<{ type: 'public-key'; id: string }>;
  userVerification?: UserVerificationRequirement;
  timeout?: number;
}

/** 登録: navigator.credentials.create → サーバーに送る JSON */
export async function createPasskey(o: CreationJson) {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: fromBase64Url(o.challenge),
      rp: o.rp,
      user: { ...o.user, id: fromBase64Url(o.user.id) },
      pubKeyCredParams: o.pubKeyCredParams,
      authenticatorSelection: o.authenticatorSelection,
      attestation: o.attestation,
      timeout: o.timeout,
      excludeCredentials: (o.excludeCredentials ?? []).map((c) => ({ type: c.type, id: fromBase64Url(c.id) }))
    }
  })) as PublicKeyCredential | null;
  if (!cred) throw new DOMException('No passkey was created', 'NotAllowedError');
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: toBase64Url(cred.rawId),
    type: 'public-key' as const,
    response: { clientDataJSON: toBase64Url(r.clientDataJSON), attestationObject: toBase64Url(r.attestationObject) }
  };
}

/** 確認: navigator.credentials.get → サーバーに送る JSON */
export async function getPasskeyAssertion(o: RequestJson) {
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge: fromBase64Url(o.challenge),
      rpId: o.rpId,
      allowCredentials: (o.allowCredentials ?? []).map((c) => ({ type: c.type, id: fromBase64Url(c.id) })),
      userVerification: o.userVerification,
      timeout: o.timeout
    }
  })) as PublicKeyCredential | null;
  if (!cred) throw new DOMException('No passkey was used', 'NotAllowedError');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: toBase64Url(cred.rawId),
    type: 'public-key' as const,
    response: {
      clientDataJSON: toBase64Url(r.clientDataJSON),
      authenticatorData: toBase64Url(r.authenticatorData),
      signature: toBase64Url(r.signature),
      userHandle: r.userHandle ? toBase64Url(r.userHandle) : null
    }
  };
}

/** 本人がパスキーの画面を閉じた / 時間切れ（エラーではなく「やめた」として静かに扱う） */
export const isPasskeyCancel = (e: unknown) => e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'AbortError');
